import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";

export interface TooltipProps {
  /** The hover/focus target (a label, icon, etc.). */
  children: ReactNode;
  /** The help text shown on hover or keyboard focus. */
  content: ReactNode;
  /**
   * Horizontal anchor of the bubble relative to the trigger. `start` keeps
   * the bubble flush-left (good for left-edge triggers), `end` flush-right
   * (good for a right-aligned info icon), `center` (default) centers it.
   */
  align?: "center" | "start" | "end";
  /**
   * Vertical placement relative to the trigger. `top` (default) opens
   * upward — the original behavior, fine when there's room above (a docked
   * panel row). `bottom` opens downward — use this when the trigger sits
   * near the top of a scroll container (e.g. a modal header): opening
   * upward there gets clipped by the container's `overflow-hidden` with
   * nothing but the header above it to absorb the bubble.
   */
  side?: "top" | "bottom";
  /** Bubble max-width override (default `max-w-[220px]`, sized for a
   * narrow docked panel). Widen for longer copy, e.g. a modal-header
   * explanation. */
  maxWidthClassName?: string;
  /**
   * OPT IN to make the bubble STICK when the trigger is clicked.
   *
   * Hover-only notes are unreachable on touch and awkward on a desktop: the
   * bubble is `pointer-events-none` with a gap above it, so moving toward it to
   * read a long note dismisses it. The operator's report was blunt — "los info
   * icon no funcionan" — and they were right in the way that matters: clicking
   * an ⓘ, which is what everyone tries first, did nothing at all.
   *
   * With this on, click toggles and the bubble stays until Escape or a click
   * outside. Hover still previews, so the fast desktop path is unchanged. A
   * pinned bubble also regains `pointer-events`, so its text can be selected
   * and any link inside it can be followed — impossible while it was a pure
   * hover surface.
   */
  clickToPin?: boolean;
  /**
   * OPT IN when the trigger lives inside a scrolling container.
   *
   * The default bubble is `position: absolute`, so it is clipped by the nearest
   * ancestor whose overflow is not `visible` — which is every scroll box. The
   * contacts table puts its rows inside `max-h-[70vh] overflow-auto`, so a hint
   * on one of the last visible rows opened downward into the container's edge
   * and was cut in half (operator, 2026-09-08).
   *
   * This switches the bubble to `position: fixed` and measures the trigger on
   * open. Fixed elements are NOT clipped by an ancestor's overflow, so the
   * bubble escapes the scroll box — WITHOUT a portal, which is the constraint
   * that shapes this whole component: a portal to `document.body` escapes the
   * extension's shadow root and loses its styles. Nothing here leaves the DOM
   * it was rendered into.
   *
   * OFF by default, deliberately. It costs a `getBoundingClientRect` per open
   * and it stops tracking if the container scrolls WHILE the bubble is up (the
   * bubble is measured once, on hover; scrolling with a tooltip open is not a
   * gesture anyone makes with a pointer, and keyboard focus scrolls the trigger
   * into view before the bubble opens). Every existing call site keeps the pure
   * CSS path, so nothing outside a scroll container changes behaviour.
   *
   * It relies on no ancestor establishing a containing block for fixed
   * positioning — a `transform`, `filter`, `perspective`, `contain` or
   * `will-change` on any parent would trap the bubble again. None exists on the
   * contacts table's chain; a future one would reintroduce the clipping this
   * fixes, silently.
   */
  escapesOverflow?: boolean;
  className?: string;
  /**
   * Extra classes for the focusable TRIGGER span (the one carrying
   * `tabIndex` and `aria-describedby`), appended to its own. `className` styles
   * the outer wrapper; this is for a trigger that must FILL it — e.g. a
   * segment of a proportional bar, where the trigger is the segment
   * (`flex-1 self-stretch`). No transform classes here either, for the same
   * containing-block reason as `className`.
   */
  triggerClassName?: string;
}

/**
 * Tooltip — a lightweight contextual-help bubble for the View 3 section titles
 * and the "About stopping" info icon (2026-06-10 v19b).
 *
 * Pure CSS visibility (`group-hover` / `group-focus-within`) with no portal or
 * JS positioning, so it works inside the extension's Shadow DOM where a
 * portalled overlay would escape the shadow root. Semantic tokens only.
 *
 * Accessibility: the trigger is focusable and wired to the bubble via
 * `aria-describedby`; the bubble carries `role="tooltip"` and shows on hover
 * AND keyboard focus. Edge-anchoring (`align`) keeps wide bubbles inside the
 * fixed-width panel rather than overflowing it.
 */
export function Tooltip({
  children,
  content,
  align = "center",
  side = "top",
  maxWidthClassName = "max-w-[220px]",
  escapesOverflow = false,
  clickToPin = false,
  className,
  triggerClassName,
}: TooltipProps) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  // Viewport coordinates for the `escapesOverflow` path, measured when the
  // bubble opens. `null` until then, which is also the state the CSS path stays
  // in forever.
  const [fixedAt, setFixedAt] = useState<{ top: number; left: number } | null>(null);
  // Whether the `escapesOverflow` bubble is currently revealed. The CSS path
  // needs no such state — `group-hover` is the whole mechanism — but a bubble
  // positioned from a MEASUREMENT has to know when to re-measure, and "is it
  // showing" is not a question CSS can answer to JS.
  const [open, setOpen] = useState(false);
  // Pinned by a click, as opposed to `open`, which hover also sets. The two are
  // separate because a pinned bubble must survive the pointer leaving.
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  // Measured to clamp the bubble inside the viewport — see `measure`.
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const measure = useCallback(() => {
    if (!escapesOverflow) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // GAP mirrors the CSS path's `mb-1.5` / `mt-1.5` so the two placements sit
    // identically; the horizontal anchor mirrors `align` for the same reason.
    const GAP = 6;
    const raw =
      align === "start" ? rect.left : align === "end" ? rect.right : rect.left + rect.width / 2;

    // CLAMP TO THE VIEWPORT, and this is not polish.
    //
    // The bubble is `position: fixed`, so a `left` past the right edge does not
    // get clipped — it EXTENDS THE PAGE, the document gains a horizontal
    // scrollbar, and everything shifts sideways. The operator saw the compare
    // modal cut off down its left side and read it as the modal being broken;
    // the modal was fine, the viewport had scrolled (2026-09-08).
    //
    // The anchor point differs per `align` because the bubble is translated in
    // CSS afterwards: `start` puts its LEFT edge here, `end` its RIGHT edge
    // (`-translate-x-full`), `center` its MIDDLE (`-translate-x-1/2`). So the
    // room each one needs on either side is different, and clamping a single
    // coordinate without accounting for that would just move the overflow to
    // the other edge.
    const width = bubbleRef.current?.getBoundingClientRect().width ?? 0;
    const MARGIN = 8;
    const spaceLeft = align === "start" ? 0 : align === "end" ? width : width / 2;
    const spaceRight = align === "start" ? width : align === "end" ? 0 : width / 2;
    const min = MARGIN + spaceLeft;
    const max = window.innerWidth - MARGIN - spaceRight;
    // `max < min` means the bubble is wider than the viewport; pinning it to
    // `min` keeps its left edge on screen, which is the readable half.
    const left = max < min ? min : Math.min(Math.max(raw, min), max);

    // THE SAME CLAMP, VERTICALLY — and with a flip first.
    //
    // A panel docked to the BOTTOM of the viewport puts its header trigger a
    // few hundred pixels above the edge, and a long bubble opening downward
    // from it ran off the bottom of the screen with its last lines unreachable
    // (the ChopBook guide, operator 2026-10-13). So: if the bubble does not fit
    // on its own `side` but fits on the other, it opens there; if it fits on
    // neither, it is slid back inside the viewport, where it may cover the
    // trigger — which beats being cut off.
    //
    // `top` is the bubble's TOP edge for `side="bottom"` and its BOTTOM edge
    // for `side="top"` (the class adds `-translate-y-full` there), so "the
    // other side" is expressed by offsetting the coordinate by the bubble's
    // height rather than by swapping classes mid-hover.
    //
    // A height of 0 means the bubble has not been laid out (jsdom, or the frame
    // before it is revealed): nothing to fit, so the plain placement stands.
    const height = bubbleRef.current?.getBoundingClientRect().height || 0;
    const below = rect.bottom + GAP;
    const above = rect.top - GAP;
    const fitsBelow = below + height <= window.innerHeight - MARGIN;
    const fitsAbove = above - height >= MARGIN;
    // Fits on neither side: slide it back inside the viewport, bottom edge on
    // the margin. A bubble too tall for even that (it is capped at the
    // viewport height by `max-h-screen` and scrolls) gives up the margin
    // rather than its last lines.
    const room = window.innerHeight - height;
    const tallTop =
      room >= 2 * MARGIN ? window.innerHeight - MARGIN - height : Math.max(0, Math.min(MARGIN, room));
    let top: number;
    if (side === "bottom") {
      top =
        fitsBelow || height === 0
          ? below
          : fitsAbove
            ? above - height
            : tallTop;
    } else {
      top =
        fitsAbove || height === 0
          ? above
          : fitsBelow
            ? below + height
            : tallTop + height;
    }

    setFixedAt({ top, left });
  }, [escapesOverflow, side, align]);

  // RE-MEASURE WHILE OPEN, and this is the whole bug fix.
  //
  // `position: fixed` is viewport-relative, and the coordinates were taken once
  // on hover. Scroll the container under the pointer — the compare modal's body
  // is `overflow-y-auto`, so every tooltip in that modal lives inside a
  // scroller — and the trigger moves while the bubble stays pinned to where the
  // trigger USED to be. It reads as the tooltip "disappearing"; really it is
  // still open, several hundred pixels away.
  //
  // `capture: true` because scroll does NOT bubble: a listener on `window`
  // never hears an inner element scrolling, and the inner scroller is exactly
  // the case this exists for. Capture sees it on the way down.
  //
  // `passive: true` — this only reads layout, never calls `preventDefault`, and
  // saying so keeps it off the scrolling critical path.
  useEffect(() => {
    // `open || pinned`, matching the dependency array below. The guard used to
    // say `!open` alone while the deps already said `open || pinned` — so a
    // PINNED bubble had its listener torn down the moment the pointer left
    // (`onMouseLeave` sets `open=false`), and then stranded on the next scroll.
    // That is the exact bug the hover path was fixed for, still live on the
    // click path that shipped in the same round, and the mismatch read as
    // deliberate. Reported by iter-4 review (A-N2, C-minor).
    if (!escapesOverflow || !(open || pinned)) return;
    const onMove = () => measure();
    window.addEventListener("scroll", onMove, { capture: true, passive: true });
    window.addEventListener("resize", onMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", onMove, { capture: true });
      window.removeEventListener("resize", onMove);
    };
  }, [escapesOverflow, open, pinned, measure]);

  // Escape and outside-click close a pinned bubble — the two exits every
  // dismissible overlay owes its user. `pointerdown` rather than `click` so the
  // bubble is gone before the press completes, which is what makes clicking
  // straight onto another control feel like one action instead of two.
  useEffect(() => {
    if (!pinned) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The innermost overlay takes the Escape. Without this a pinned bubble
      // inside a `DockablePanel` closed AND minimized the whole panel on one
      // key press, because both listen on `document` (PR #1054 review, C-M1).
      // Capture phase (below) is what makes the stop reach a listener that was
      // registered on the same node before this one.
      event.stopPropagation();
      setPinned(false);
    };
    const onDown = (event: MouseEvent) => {
      // `composedPath()`, NOT `event.target`. Inside a shadow root a listener
      // on `document` sees `target` RETARGETED to the shadow host, so
      // `contains(target)` is false for every press — including one on the
      // trigger or inside the bubble. In the extension panels that made a
      // pinned bubble impossible to close from its own trigger (pointerdown
      // unpinned, the click re-pinned) and dismissed it on any press inside
      // it (PR #1054 review, A-2 / C-I1). The composed path still contains
      // the real nodes. `target` stays as the fallback for an environment
      // without it.
      const wrap = wrapRef.current;
      if (!wrap) return;
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      const inside = path.length > 0 ? path.includes(wrap) : wrap.contains(event.target as Node);
      if (!inside) setPinned(false);
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [pinned]);

  const anchor =
    align === "start" ? "left-0" : align === "end" ? "right-0" : "left-1/2 -translate-x-1/2";
  const placement = side === "bottom" ? "top-full mt-1.5" : "bottom-full mb-1.5";
  // A TRANSFORM HERE SILENTLY BREAKS `escapesOverflow`, so the class the
  // caller passes is checked rather than trusted.
  //
  // `transform` / `filter` / `backdrop-filter` / `perspective` / `contain` on
  // an ancestor make that element the CONTAINING BLOCK for a `position: fixed`
  // descendant (CSS Transforms §3). The bubble's measured VIEWPORT coordinates
  // are then read as offsets inside a 14px icon, and the note lands nowhere
  // near its trigger. `InfoHint` passed `-translate-y-1` here for months and
  // that is exactly what happened; three correct fixes to the positioning code
  // could not reach it, because the positioning was never the problem.
  //
  // jsdom cannot catch this — it computes no layout, so `fixed` resolves
  // against nothing and the containing block cannot exist to be wrong. Hence a
  // dev-time assertion on the CLASS instead of a test on the behaviour.
  if (process.env.NODE_ENV !== "production" && escapesOverflow && className) {
    const breaks =
      /(^|\s|:)(-?translate-|scale-|rotate-|skew-|transform\b|filter\b|backdrop-|perspective-|contain-)/;
    if (breaks.test(className)) {
      console.error(
        `Tooltip: className ${JSON.stringify(className)} creates a containing block, ` +
          "which anchors the escapesOverflow bubble to the trigger instead of the " +
          "viewport. Move the transform onto the trigger's own child."
      );
    }
  }
  const wrap = ["relative inline-flex items-center group", className].filter(Boolean).join(" ");
  // The `escapesOverflow` bubble is positioned from the measured rect, so it
  // takes NEITHER the `absolute` placement classes nor the anchor ones — those
  // two are the whole CSS path. It keeps every visual class, so the two
  // strategies look identical; only what confines them differs.
  const positioning = escapesOverflow
    ? `fixed max-h-screen overflow-y-auto ${anchor === "left-1/2 -translate-x-1/2" ? "-translate-x-1/2" : ""} ${
        side === "bottom" ? "" : "-translate-y-full"
      } ${anchor === "right-0" ? "-translate-x-full" : ""}`
    : `absolute ${placement} ${anchor}`;
  return (
    <span ref={wrapRef} className={wrap}>
      <span
        ref={triggerRef}
        // The tooltip trigger must be keyboard-focusable so the help bubble
        // reveals on focus (group-focus-within), not only on hover. It carries
        // aria-describedby pointing at the role="tooltip" bubble.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: focusable tooltip trigger by design
        tabIndex={0}
        aria-describedby={id}
        // Measured on the same two events the CSS path reveals on, so the
        // coordinates are always the ones the bubble is about to be shown at.
        onMouseEnter={() => {
          measure();
          setOpen(true);
        }}
        onFocus={() => {
          measure();
          setOpen(true);
        }}
        // Closing matters as much as opening: without it the scroll listener
        // would outlive the bubble and re-measure a tooltip nobody is looking
        // at, once per scroll event, for the life of the page.
        onMouseLeave={() => setOpen(false)}
        onBlur={() => setOpen(false)}
        // The click surface. `onClick` on the WRAPPER span, not on whatever the
        // caller passed as children: `InfoHint` hands us a real `<button>`, and
        // an inner handler would fight it for the same press.
        onClick={
          clickToPin
            ? () => {
                measure();
                setPinned((prev) => !prev);
              }
            : undefined
        }
        // A pinned bubble is a disclosure, and `aria-expanded` is how that is
        // announced. Absent entirely when the tooltip is hover-only, because
        // then there is nothing the user can expand.
        aria-expanded={clickToPin ? pinned : undefined}
        className={[
          "inline-flex items-center gap-1 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-accent",
          triggerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </span>
      <span
        ref={bubbleRef}
        role="tooltip"
        id={id}
        // The ONLY inline style in this component, and it is unavoidable: these
        // are measured viewport pixels, which no utility class can express. The
        // project's no-inline-style rule is about STATIC styling living in
        // tokens — everything static here is still a class.
        // `visibility: hidden` for the frame between "revealed" and "measured".
        // A `fixed` element with no `top`/`left` renders at its static position,
        // which for this one is wherever the flow left it — visibly wrong for
        // one paint. It stays laid out (so nothing reflows when it appears),
        // just not painted.
        style={
          escapesOverflow
            ? fixedAt
              ? { top: `${fixedAt.top}px`, left: `${fixedAt.left}px` }
              : { visibility: "hidden" }
            : undefined
        }
        // A PINNED bubble drops `pointer-events-none` and stops depending on
        // `group-hover` — that pair is what made a long note unreadable: it
        // vanished the moment the pointer crossed the gap toward it, and its
        // text could not be selected.
        //
        // `block` OR `hidden`, NEVER BOTH. The first cut emitted
        // `hidden … ${pinned ? "block" : ""}` and a comment claiming block won
        // "because it comes later in the class list". That is not how CSS
        // works: the attribute's order is irrelevant, precedence comes from the
        // generated stylesheet, and Tailwind emits `hidden` after `block` — so
        // `hidden` won and the pinned bubble never appeared. Reported as "el
        // info icon no hace nada" (operator, 2026-09-08) on a feature whose
        // tests passed, because they asserted the class STRING contained
        // "block" rather than that the element could be displayed.
        className={`${pinned ? "" : "pointer-events-none"} ${positioning} z-50 ${
          pinned ? "block" : "hidden"
        } w-max ${maxWidthClassName} rounded-sm border border-border bg-bg px-2.5 py-1.5 text-left text-caption leading-snug text-text shadow-md group-hover:block group-focus-within:block`}
      >
        {content}
      </span>
    </span>
  );
}
